package com.samrudha.portfolio.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.samrudha.portfolio.components.layouts.PageLayoutData
import com.samrudha.portfolio.toSitePalette
import org.jetbrains.compose.web.css.JustifyContent

// ─── Hero ──────────────────────────────────────────────────────────────────────

val HeroWrapStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .display(DisplayStyle.Flex)
        .flexDirection(FlexDirection.Column)
        .justifyContent(JustifyContent.Center)
        .padding(top = 6.cssRem, bottom = 4.cssRem)
}

val HeroTaglineStyle = CssStyle.base {
    Modifier
        .fontFamily("'JetBrains Mono'", "monospace")
        .fontSize(0.9.cssRem)           // ↑ was 0.78
        .letterSpacing(0.18.cssRem)
        .textTransform(TextTransform.Uppercase)
        .opacity(0.6)                   // ↑ was 0.4
        .margin(bottom = 2.cssRem)
}

val HeroH1Style = CssStyle {
    base {
        Modifier
            .fontFamily("Nunito", "sans-serif")
            .fontSize(4.cssRem)
            .fontWeight(FontWeight.Normal)
            .lineHeight(1.08)
            .margin(bottom = 0.cssRem)
    }
    Breakpoint.MD { Modifier.fontSize(6.5.cssRem) }
}

val HeroSubStyle = CssStyle.base {
    Modifier
        .fontFamily("'JetBrains Mono'", "monospace")
        .fontSize(1.05.cssRem)          // ↑ was 0.9
        .letterSpacing(0.02.cssRem)
        .lineHeight(1.85)
        .opacity(0.75)                  // ↑ was 0.55
        .maxWidth(46.cssRem)
        .margin(top = 2.cssRem, bottom = 3.5.cssRem)
}

val HeroCtaStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .flexWrap(FlexWrap.Wrap)
        .gap(1.cssRem)
        .alignItems(org.jetbrains.compose.web.css.AlignItems.Center)
}

// ─── Divider / label ──────────────────────────────────────────────────────────

val DividerLabelStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .display(DisplayStyle.Flex)
        .alignItems(org.jetbrains.compose.web.css.AlignItems.Center)
        .gap(1.5.cssRem)
        .margin(topBottom = 2.5.cssRem)
}

val DividerTextStyle = CssStyle.base {
    Modifier
        .fontFamily("'JetBrains Mono'", "monospace")
        .fontSize(0.85.cssRem)          // ↑ was 0.68
        .letterSpacing(0.18.cssRem)
        .textTransform(TextTransform.Uppercase)
        .opacity(0.5)                   // ↑ was 0.3
        .whiteSpace(WhiteSpace.NoWrap)
}

// ─── Problem section ──────────────────────────────────────────────────────────

val ProblemSectionStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .margin(bottom = 3.cssRem)
}

val ProblemHeadingStyle = CssStyle {
    base {
        Modifier
            .fontFamily("Nunito", "sans-serif")
            .fontSize(2.8.cssRem)
            .fontWeight(FontWeight.Normal)
            .lineHeight(1.2)
            .margin(bottom = 3.cssRem)
    }
    Breakpoint.MD { Modifier.fontSize(3.8.cssRem) }
}

val ProblemGridStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Grid)
            .gridTemplateColumns { size(1.fr) }
            .gap(0.px)
    }
    Breakpoint.MD {
        Modifier.gridTemplateColumns { repeat(2) { size(1.fr) } }
    }
}

val ProblemCardNumStyle = CssStyle.base {
    Modifier
        .fontFamily("'JetBrains Mono'", "monospace")
        .fontSize(0.82.cssRem)          // ↑ was 0.68
        .opacity(0.45)                  // ↑ was 0.28
        .margin(bottom = 1.cssRem)
        .display(DisplayStyle.Block)
}

val ProblemCardTitleStyle = CssStyle.base {
    Modifier
        .fontFamily("Nunito", "sans-serif")
        .fontSize(1.4.cssRem)           // ↑ was 1.25
        .fontWeight(FontWeight.Normal)
        .margin(bottom = 0.7.cssRem)
}

val ProblemCardBodyStyle = CssStyle.base {
    Modifier
        .fontSize(1.0.cssRem)           // ↑ was 0.92
        .lineHeight(1.82)
        .opacity(0.75)                  // ↑ was 0.6
}

// ─── Stack / Stats section ────────────────────────────────────────────────────

val StackSectionStyle = CssStyle.base {
    Modifier.fillMaxWidth().margin(bottom = 3.cssRem)
}

val StackHeadingStyle = CssStyle {
    base {
        Modifier
            .fontFamily("Nunito", "sans-serif")
            .fontSize(2.8.cssRem)
            .fontWeight(FontWeight.Normal)
            .lineHeight(1.2)
            .margin(bottom = 3.cssRem)
    }
    Breakpoint.MD { Modifier.fontSize(3.8.cssRem) }
}

val StackGridStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Grid)
            .gridTemplateColumns { size(1.fr) }
            .gap(0.px)
    }
    Breakpoint.MD {
        Modifier.gridTemplateColumns { repeat(3) { size(1.fr) } }
    }
}

val StackStatStyle = CssStyle.base {
    Modifier
        .fontFamily("Nunito", "sans-serif")
        .fontSize(2.4.cssRem)
        .fontWeight(FontWeight.Normal)
        .margin(bottom = 0.4.cssRem)
}

val StackStatLabelStyle = CssStyle.base {
    Modifier
        .fontFamily("'JetBrains Mono'", "monospace")
        .fontSize(0.85.cssRem)          // ↑ was 0.7
        .letterSpacing(0.1.cssRem)
        .textTransform(TextTransform.Uppercase)
        .opacity(0.55)                  // ↑ was 0.38
        .margin(bottom = 1.2.cssRem)
        .display(DisplayStyle.Block)
}

val StackCardDescStyle = CssStyle.base {
    Modifier
        .fontSize(1.0.cssRem)           // ↑ was 0.88
        .lineHeight(1.78)
        .opacity(0.7)                   // ↑ was 0.55
}

// ─── Projects section ─────────────────────────────────────────────────────────

val ProjectsSectionStyle = CssStyle.base {
    Modifier.fillMaxWidth().margin(bottom = 3.cssRem)
}

val ProjectsHeadingStyle = CssStyle {
    base {
        Modifier
            .fontFamily("Nunito", "sans-serif")
            .fontSize(2.8.cssRem)
            .fontWeight(FontWeight.Normal)
            .lineHeight(1.2)
            .margin(bottom = 0.5.cssRem)
    }
    Breakpoint.MD { Modifier.fontSize(3.8.cssRem) }
}

val ProjectsSubheadStyle = CssStyle.base {
    Modifier
        .fontFamily("'JetBrains Mono'", "monospace")
        .fontSize(0.95.cssRem)          // ↑ was 0.82
        .opacity(0.58)                  // ↑ was 0.4
        .margin(bottom = 3.cssRem)
}

val ProjectRowInnerStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .gap(0.5.cssRem)
    }
    Breakpoint.MD {
        Modifier.flexDirection(FlexDirection.Row)
            .alignItems(org.jetbrains.compose.web.css.AlignItems.Baseline)
            .gap(2.cssRem)
    }
}

val ProjectNumStyle = CssStyle.base {
    Modifier
        .fontFamily("'JetBrains Mono'", "monospace")
        .fontSize(0.85.cssRem)          // ↑ was 0.68
        .opacity(0.45)                  // ↑ was 0.28
        .minWidth(2.5.cssRem)
}

val ProjectNameStyle = CssStyle {
    base {
        Modifier
            .fontFamily("Nunito", "sans-serif")
            .fontSize(1.75.cssRem)      // ↑ was 1.6
            .fontWeight(FontWeight.Normal)
            .flex(1)
    }
    Breakpoint.MD { Modifier.fontSize(2.2.cssRem) }
}

val ProjectTagStyle = CssStyle.base {
    Modifier
        .fontFamily("'JetBrains Mono'", "monospace")
        .fontSize(0.85.cssRem)          // ↑ was 0.7
        .letterSpacing(0.04.cssRem)
        .opacity(0.55)                  // ↑ was 0.38
}

val ProjectDescStyle2 = CssStyle.base {
    Modifier
        .fontSize(1.02.cssRem)          // ↑ was 0.9
        .lineHeight(1.82)
        .opacity(0.72)                  // ↑ was 0.58
        .maxWidth(52.cssRem)
}

// ─── Manifesto section ────────────────────────────────────────────────────────

val ManifestoSectionStyle = CssStyle.base {
    Modifier.fillMaxWidth().margin(bottom = 3.cssRem)
}

val ManifestoHeadingStyle = CssStyle {
    base {
        Modifier
            .fontFamily("Nunito", "sans-serif")
            .fontSize(2.4.cssRem)
            .fontWeight(FontWeight.Normal)
            .lineHeight(1.2)
            .margin(bottom = 2.cssRem)
    }
    Breakpoint.MD { Modifier.fontSize(3.2.cssRem) }
}

val CtaHeadingStyle = CssStyle {
    base {
        Modifier
            .fontFamily("Nunito", "sans-serif")
            .fontSize(2.8.cssRem)
            .fontWeight(FontWeight.Normal)
            .lineHeight(1.2)
            .margin(bottom = 2.cssRem)
    }
    Breakpoint.MD { Modifier.fontSize(4.cssRem) }
}

val CtaLinksStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .flexWrap(FlexWrap.Wrap)
        .gap(2.cssRem)
}

// ─── Shared button / link helpers ─────────────────────────────────────────────

private fun primaryBtnModifier(accent: Color) =
    Modifier
        .fontFamily("'JetBrains Mono'", "monospace")
        .fontSize(0.9.cssRem)           // ↑ was 0.78
        .letterSpacing(0.1.cssRem)
        .textTransform(TextTransform.Uppercase)
        .padding(leftRight = 1.8.cssRem, topBottom = 0.9.cssRem)
        .backgroundColor(accent)
        .color(Colors.Black)
        .border(0.px)
        .borderRadius(0.px)
        .cursor(Cursor.Pointer)
        .textDecorationLine(TextDecorationLine.None)
        .display(DisplayStyle.InlineBlock)

private fun ghostBtnModifier(accent: Color) =
    Modifier
        .fontFamily("'JetBrains Mono'", "monospace")
        .fontSize(0.9.cssRem)           // ↑ was 0.78
        .letterSpacing(0.1.cssRem)
        .textTransform(TextTransform.Uppercase)
        .padding(leftRight = 1.8.cssRem, topBottom = 0.9.cssRem)
        .backgroundColor(Colors.Transparent)
        .color(accent)
        .border(1.px, LineStyle.Solid, accent)
        .borderRadius(0.px)
        .cursor(Cursor.Pointer)
        .textDecorationLine(TextDecorationLine.None)
        .display(DisplayStyle.InlineBlock)

private fun quietLinkModifier() =
    Modifier
        .fontFamily("'JetBrains Mono'", "monospace")
        .fontSize(0.92.cssRem)          // ↑ was 0.82
        .letterSpacing(0.06.cssRem)
        .opacity(0.62)                  // ↑ was 0.45
        .textDecorationLine(TextDecorationLine.None)
        .display(DisplayStyle.InlineBlock)

// ─── Data ─────────────────────────────────────────────────────────────────────

private data class ProblemCard(val num: String, val title: String, val body: String)
private val problemCards = listOf(
    ProblemCard("01", "On-device AI, not cloud AI",
        "Quantized LLMs running entirely on-device via JNI/NDK. Private, fast, and functional with zero signal. No API call. No latency. No data leaving the phone."),
    ProblemCard("02", "Offline-first, not online-first",
        "Room + Retrofit sync designed to survive disconnection. Users in low-connectivity environments get the full experience — state reconciles silently when signal returns."),
    ProblemCard("03", "Owned architecture, not committee decisions",
        "Co-founded and built Genzopia solo. Every product decision, architecture choice, and monetization experiment was mine to make and mine to live with."),
    ProblemCard("04", "Real users, not demo users",
        "65,000+ organic installs. 4.9 ★ across 10+ countries. Production-grade crash cycles, ANR resolutions, and Profiler sessions — not simulated load tests."),
)

private data class StackCard(val stat: String, val label: String, val desc: String)
private val stackCards = listOf(
    StackCard("65K+", "Organic users", "Built and scaled Offline AI entirely solo. No paid acquisition. Product-market fit earned through iteration."),
    StackCard("4.9 ★", "FocusGuard rating", "Custom Android launcher. OS-level app blocking, per-app time limits, Stripe integration. 10+ countries."),
    StackCard("2yr", "Production experience", "Kotlin, Jetpack Compose, MVVM/MVI, Coroutines/Flow, Room, Retrofit, WorkManager, JNI/NDK."),
    StackCard("4", "Shipped apps", "Offline AI · FocusGuard · GenzCrop · Device Doctor. One on Amazon Appstore. One on Play Store with 30K+ installs."),
    StackCard("0", "Cloud calls in Offline AI", "Every inference runs on-device. ONNX + quantized models + semantic search. The network permission is not requested."),
    StackCard("2", "Hackathon 1st places", "Ideathon 1.0 & 2.0. University Project Competition. Competing, winning, shipping."),
)

private data class Project(val num: String, val name: String, val tags: String, val desc: String, val link: String)
private val projects = listOf(
    Project("01", "Offline AI", "Kotlin · NDK · Jetpack Compose",
        "Private AI assistant. Quantized LLMs on-device via custom JNI/NDK inference pipeline. Semantic search over local embeddings. No cloud calls. 65,000+ users.",
        "https://play.google.com/store/apps/details?id=com.genzopia.offlineai"),
    Project("02", "FocusAI", "Kotlin · Compose · AIDL",
        "Custom Android launcher with OS-level app blocking and per-app time limits. Full payment integration via Stripe. Built UI/UX from scratch. 4.9★ in 10+ countries.",
        "https://play.google.com/store/apps/details?id=com.genzopia.addiction"),
    Project("04", "Device Doctor", "Android Hardware APIs · Amazon Appstore",
        "Real-time hardware diagnostics for camera, microphone, and sensors. Published on Amazon Appstore.",
        "https://www.amazon.in/JSR-Solutions-Device-Doctor/dp/B0CN1X6QY1/"),
)

// ─── Page ──────────────────────────────────────────────────────────────────────

@InitRoute
fun initHomePage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Home"))
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun HomePage() {
    val sitePalette = ColorMode.current.toSitePalette()
    val accent = sitePalette.brand.accent
    val colorMode = ColorMode.current
    val ctx = rememberPageContext()

    val border      = if (colorMode.isLight) Color.rgba(0, 0, 0, 35)  else Color.rgba(255, 255, 255, 20)
    val borderFaint = if (colorMode.isLight) Color.rgba(0, 0, 0, 15)  else Color.rgba(255, 255, 255, 8)
    val codeBg      = if (colorMode.isLight) Color.rgba(0, 0, 0, 4)   else Color.rgba(255, 255, 255, 3)

    Column(Modifier.fillMaxWidth()) {

        // ── Hero ──────────────────────────────────────────────────────────────
        Div(HeroWrapStyle.toAttrs()) {
            Div(attrs = Modifier
                .fillMaxWidth()
                .display(DisplayStyle.Flex)
                .flexDirection(FlexDirection.Row)
                .alignItems(org.jetbrains.compose.web.css.AlignItems.Center)
                .justifyContent(JustifyContent.SpaceBetween)
                .gap(4.cssRem)
                .flexWrap(FlexWrap.Wrap)
                .toAttrs()
            ) {
                Div(attrs = Modifier.flex(1).minWidth(280.px).toAttrs()) {
                    P(HeroTaglineStyle.toAttrs()) { Text("Android Engineer · Startup Founder · Pune, India") }

                    Div(HeroH1Style.toAttrs()) {
                        SpanText("Building apps")
                        Br {}
                        SpanText("that live")
                        Br {}
                        SpanText("on the device.", Modifier.color(accent))
                    }

                    P(HeroSubStyle.toAttrs()) {
                        Text(
                            "2 years. 65,000 organic users. Three shipped apps. " +
                                    "On-device LLMs, offline-first sync, and zero cloud dependencies. " +
                                    "Co-founded Genzopia Solutions and owned every layer: product, architecture, monetization."
                        )
                    }

                    Div(HeroCtaStyle.toAttrs()) {
                        Button(
                            attrs = primaryBtnModifier(accent)
                                .onClick { ctx.router.tryRoutingTo("/about") }
                                .toAttrs()
                        ) { Text("See the work") }
                        A(
                            href = "https://github.com/samrudha01codespace",
                            attrs = ghostBtnModifier(accent).toAttrs()
                        ) { Text("GitHub ↗") }
                        A(
                            href = "mailto:samrudhakshirsagar@gmail.com",
                            attrs = quietLinkModifier().toAttrs()
                        ) { Text("samrudhakshirsagar@gmail.com") }
                    }
                }

                Div(attrs = Modifier
                    .width(300.px)
                    .height(300.px)
                    .minWidth(300.px)
                    .borderRadius(50.percent)
                    .border(2.px, LineStyle.Solid, accent)
                    .padding(5.px)
                    .toAttrs()
                ) {
                    Img(
                        src = "/samrudha.jpeg",
                        alt = "Samrudha Kshirsagar",
                        attrs = Modifier
                            .width(100.percent)
                            .height(100.percent)
                            .borderRadius(50.percent)
                            .styleModifier {
                                property("object-fit", "cover")
                                property("display", "block")
                            }
                            .toAttrs()
                    )
                }
            }
        }

        // ── Divider ───────────────────────────────────────────────────────────
        Div(DividerLabelStyle.toAttrs()) {
            Div(attrs = Modifier.flex(1).height(1.px).backgroundColor(border).toAttrs()) {}
            Span(DividerTextStyle.toAttrs()) { Text("What I actually believe") }
            Div(attrs = Modifier.flex(1).height(1.px).backgroundColor(border).toAttrs()) {}
        }

        // ── Problem section ───────────────────────────────────────────────────
        Div(ProblemSectionStyle.toAttrs()) {
            Div(ProblemHeadingStyle.toAttrs()) {
                SpanText("The best mobile apps")
                Br {}
                SpanText("work without the cloud.")
            }
            Div(ProblemGridStyle.toAttrs()) {
                problemCards.forEach { card ->
                    Div(attrs = Modifier
                        .padding(2.cssRem)
                        .borderTop(1.px, LineStyle.Solid, border)
                        .toAttrs()
                    ) {
                        Span(ProblemCardNumStyle.toAttrs()) { Text(card.num) }
                        P(ProblemCardTitleStyle.toModifier().color(accent).toAttrs()) { Text(card.title) }
                        P(ProblemCardBodyStyle.toAttrs()) { Text(card.body) }
                    }
                }
            }
        }

        // ── Divider ───────────────────────────────────────────────────────────
        Div(DividerLabelStyle.toAttrs()) {
            Div(attrs = Modifier.flex(1).height(1.px).backgroundColor(border).toAttrs()) {}
            Span(DividerTextStyle.toAttrs()) { Text("The numbers") }
            Div(attrs = Modifier.flex(1).height(1.px).backgroundColor(border).toAttrs()) {}
        }

        // ── Stack / Stats ─────────────────────────────────────────────────────
        Div(StackSectionStyle.toAttrs()) {
            Div(StackHeadingStyle.toAttrs()) {
                SpanText("Production experience.")
                Br {}
                SpanText("Not just side projects.", Modifier.opacity(0.45))
            }
            Div(StackGridStyle.toAttrs()) {
                stackCards.forEach { card ->
                    Div(attrs = Modifier
                        .padding(2.cssRem)
                        .borderTop(1.px, LineStyle.Solid, border)
                        .borderLeft(1.px, LineStyle.Solid, borderFaint)
                        .toAttrs()
                    ) {
                        Span(StackStatStyle.toModifier().color(accent).toAttrs()) { Text(card.stat) }
                        Span(StackStatLabelStyle.toAttrs()) { Text(card.label) }
                        P(StackCardDescStyle.toAttrs()) { Text(card.desc) }
                    }
                }
            }
        }

        // ── Divider ───────────────────────────────────────────────────────────
        Div(DividerLabelStyle.toAttrs()) {
            Div(attrs = Modifier.flex(1).height(1.px).backgroundColor(border).toAttrs()) {}
            Span(DividerTextStyle.toAttrs()) { Text("Selected work") }
            Div(attrs = Modifier.flex(1).height(1.px).backgroundColor(border).toAttrs()) {}
        }

        // ── Projects ──────────────────────────────────────────────────────────
        Div(ProjectsSectionStyle.toAttrs()) {
            Div(ProjectsHeadingStyle.toAttrs()) { SpanText("Three apps shipped.") }
            P(ProjectsSubheadStyle.toAttrs()) { Text("All production. All on the Play Store or Amazon Appstore.") }
            projects.forEach { project ->
                Div(attrs = Modifier
                    .fillMaxWidth()
                    .display(DisplayStyle.Flex)
                    .flexDirection(FlexDirection.Column)
                    .padding(topBottom = 2.5.cssRem)
                    .borderTop(1.px, LineStyle.Solid, border)
                    .gap(1.cssRem)
                    .toAttrs()
                ) {
                    Div(ProjectRowInnerStyle.toAttrs()) {
                        Span(ProjectNumStyle.toAttrs()) { Text(project.num) }
                        Span(ProjectNameStyle.toModifier().color(accent).toAttrs()) { Text(project.name) }
                        Span(ProjectTagStyle.toAttrs()) { Text(project.tags) }
                    }
                    P(ProjectDescStyle2.toAttrs()) { Text(project.desc) }
                    A(
                        href = project.link,
                        attrs = quietLinkModifier().opacity(0.55).color(accent).toAttrs()
                    ) { Text("View Project →") }
                }
            }
        }

        // ── Divider ───────────────────────────────────────────────────────────
        Div(DividerLabelStyle.toAttrs()) {
            Div(attrs = Modifier.flex(1).height(1.px).backgroundColor(border).toAttrs()) {}
            Span(DividerTextStyle.toAttrs()) { Text("The architecture") }
            Div(attrs = Modifier.flex(1).height(1.px).backgroundColor(border).toAttrs()) {}
        }

        // ── Manifesto / Code block ─────────────────────────────────────────────
        Div(ManifestoSectionStyle.toAttrs()) {
            Div(ManifestoHeadingStyle.toAttrs()) {
                SpanText("The stack that makes")
                Br {}
                SpanText("offline AI possible.")
            }
            Pre(attrs = Modifier
                .fillMaxWidth()
                .fontFamily("'JetBrains Mono'", "monospace")
                .fontSize(0.95.cssRem)  // ↑ was 0.82
                .lineHeight(2.0)
                .padding(2.cssRem)
                .border(1.px, LineStyle.Solid, borderFaint)
                .backgroundColor(codeBg)
                .overflow(Overflow.Auto)
                .margin(bottom = 2.cssRem)
                .toAttrs()
            ) {
                Code {
                    Text(
                        "// Offline AI — inference pipeline\n\n" +
                                "model_format:         .task, .gguf, .litertlm\n" +
                                "inference_runtime:    JNI/NDK — custom C++ bridge\n" +
                                "embedding_search:     on-device semantic vector index\n" +
                                "network_permission:   NOT REQUESTED\n" +
                                "cloud_calls:          0\n" +
                                "data_location:        YOUR DEVICE\n\n" +
                                "// Android stack\n\n" +
                                "language:             Kotlin\n" +
                                "ui:                   Jetpack Compose\n" +
                                "architecture:         MVVM / MVI\n" +
                                "async:                Coroutines + Flow\n" +
                                "local_db:             Room\n" +
                                "sync:                 Retrofit + WorkManager\n" +
                                "ai_tools:             MediaPipe · OpenCV · ONNX · Gemma · Qwen"
                    )
                }
            }
        }

        // ── CTA ───────────────────────────────────────────────────────────────
        Div(attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 4.cssRem)
            .borderTop(1.px, LineStyle.Solid, border)
            .margin(bottom = 4.cssRem)
            .toAttrs()
        ) {
            Div(CtaHeadingStyle.toAttrs()) {
                SpanText("Building something")
                Br {}
                SpanText("that needs to work offline?")
            }
            Div(CtaLinksStyle.toAttrs()) {
                A(
                    href = "mailto:samrudhakshirsagar@gmail.com",
                    attrs = primaryBtnModifier(accent).toAttrs()
                ) { Text("Get in touch") }
                A(
                    href = "https://linkedin.com/in/samrudha-kshirsagar",
                    attrs = ghostBtnModifier(accent).toAttrs()
                ) { Text("LinkedIn ↗") }
                A(
                    href = "https://github.com/samrudha01codespace",
                    attrs = ghostBtnModifier(accent).toAttrs()
                ) { Text("GitHub") }
            }
        }
    }
}